/**
 * Caching Strategy Hook
 * 
 * React hook that provides intelligent caching strategies for different
 * data types and use cases, with automatic cache management and optimization.
 */

import { useEffect, useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import CachingStrategyService, { DataType, InvalidationTrigger, CacheStrategy } from '../services/cachingStrategyService';
import { debugWarn } from '../utils/debugLog';

// Global caching service instance
let cachingService: CachingStrategyService | null = null;

/**
 * Hook for managing caching strategies
 */
export const useCachingStrategy = () => {
  const queryClient = useQueryClient();

  // Initialize caching service
  const service = useMemo(() => {
    if (!cachingService) {
      cachingService = new CachingStrategyService(queryClient);
    }
    return cachingService;
  }, [queryClient]);

  // Setup background refresh on mount
  useEffect(() => {
    service.setupBackgroundRefresh();
    
    // Preload critical data
    service.preloadCriticalData().catch(error => {
      debugWarn('Failed to preload critical data:', error);
    });

    // Setup periodic cleanup
    const cleanupInterval = setInterval(() => {
      service.cleanupExpiredEntries();
      service.optimizeMemoryUsage();
    }, 10 * 60 * 1000); // Every 10 minutes

    return () => {
      clearInterval(cleanupInterval);
      service.destroy();
    };
  }, [service]);

  // Get strategy for data type
  const getStrategy = useCallback((dataType: DataType): CacheStrategy => {
    return service.getStrategy(dataType);
  }, [service]);

  // Apply caching strategy
  const applyCachingStrategy = useCallback((queryKeyPattern: string, dataType: DataType): CacheStrategy => {
    return service.applyCachingStrategy(queryKeyPattern, dataType);
  }, [service]);

  // Invalidate cache
  const invalidateCache = useCallback(async (
    queryKeyPattern: string | string[],
    trigger: InvalidationTrigger,
    options?: {
      exact?: boolean;
      refetch?: boolean;
      removeFromCache?: boolean;
    }
  ) => {
    return service.invalidateCache(queryKeyPattern, trigger, options);
  }, [service]);

  // Get cache metrics
  const getCacheMetrics = useCallback(() => {
    return service.getCacheMetrics();
  }, [service]);

  // Preload data
  const preloadCriticalData = useCallback(async () => {
    return service.preloadCriticalData();
  }, [service]);

  return {
    getStrategy,
    applyCachingStrategy,
    invalidateCache,
    getCacheMetrics,
    preloadCriticalData,
    service
  };
};

/**
 * Hook for product-specific caching
 */
export const useProductCaching = () => {
  const { getStrategy, invalidateCache } = useCachingStrategy();

  const catalogStrategy = useMemo(() => getStrategy(DataType.PRODUCT_CATALOG), [getStrategy]);
  const detailsStrategy = useMemo(() => getStrategy(DataType.PRODUCT_DETAILS), [getStrategy]);
  const collectionsStrategy = useMemo(() => getStrategy(DataType.COLLECTIONS), [getStrategy]);

  const invalidateProductCache = useCallback(async (productHandle?: string) => {
    if (productHandle) {
      // Invalidate specific product
      await invalidateCache(['product-detail', productHandle], InvalidationTrigger.DATA_MUTATION, {
        exact: true,
        refetch: true
      });
    } else {
      // Invalidate all product data
      await invalidateCache(['shopify-products', 'product-detail'], InvalidationTrigger.DATA_MUTATION, {
        refetch: true
      });
    }
  }, [invalidateCache]);

  const invalidateCollectionsCache = useCallback(async () => {
    await invalidateCache('shopify-collections', InvalidationTrigger.DATA_MUTATION, {
      refetch: true
    });
  }, [invalidateCache]);

  return {
    catalogStrategy,
    detailsStrategy,
    collectionsStrategy,
    invalidateProductCache,
    invalidateCollectionsCache
  };
};

/**
 * Hook for cart-specific caching
 */
export const useCartCaching = () => {
  const { getStrategy, invalidateCache } = useCachingStrategy();

  const cartStrategy = useMemo(() => getStrategy(DataType.CART_DATA), [getStrategy]);

  const invalidateCartCache = useCallback(async (cartId?: string) => {
    const queryPattern = cartId ? ['shopify-cart', cartId] : 'shopify-cart';
    
    await invalidateCache(queryPattern, InvalidationTrigger.USER_ACTION, {
      exact: !!cartId,
      refetch: true,
      removeFromCache: false
    });
  }, [invalidateCache]);

  const clearCartCache = useCallback(async (cartId?: string) => {
    const queryPattern = cartId ? ['shopify-cart', cartId] : 'shopify-cart';
    
    await invalidateCache(queryPattern, InvalidationTrigger.USER_ACTION, {
      exact: !!cartId,
      refetch: false,
      removeFromCache: true
    });
  }, [invalidateCache]);

  return {
    cartStrategy,
    invalidateCartCache,
    clearCartCache
  };
};

/**
 * Hook for content-specific caching (blog, events, etc.)
 */
export const useContentCaching = () => {
  const { getStrategy, invalidateCache } = useCachingStrategy();

  const blogStrategy = useMemo(() => getStrategy(DataType.BLOG_CONTENT), [getStrategy]);
  const eventsStrategy = useMemo(() => getStrategy(DataType.EVENTS_DATA), [getStrategy]);
  const scheduleStrategy = useMemo(() => getStrategy(DataType.SCHEDULE_DATA), [getStrategy]);
  const liveStrategy = useMemo(() => getStrategy(DataType.LIVE_DATA), [getStrategy]);

  const invalidateBlogCache = useCallback(async () => {
    await invalidateCache('blog', InvalidationTrigger.DATA_MUTATION, {
      refetch: true
    });
  }, [invalidateCache]);

  const invalidateEventsCache = useCallback(async () => {
    await invalidateCache('events', InvalidationTrigger.DATA_MUTATION, {
      refetch: true
    });
  }, [invalidateCache]);

  const invalidateScheduleCache = useCallback(async () => {
    await invalidateCache('schedule', InvalidationTrigger.TIME_BASED, {
      refetch: true
    });
  }, [invalidateCache]);

  const invalidateLiveDataCache = useCallback(async () => {
    await invalidateCache('live-show', InvalidationTrigger.TIME_BASED, {
      refetch: true
    });
  }, [invalidateCache]);

  return {
    blogStrategy,
    eventsStrategy,
    scheduleStrategy,
    liveStrategy,
    invalidateBlogCache,
    invalidateEventsCache,
    invalidateScheduleCache,
    invalidateLiveDataCache
  };
};

/**
 * Hook for cache performance monitoring
 */
export const useCacheMonitoring = () => {
  const { getCacheMetrics, service } = useCachingStrategy();

  const metrics = useMemo(() => getCacheMetrics(), [getCacheMetrics]);

  const getPerformanceSummary = useCallback(() => {
    const metricsMap = getCacheMetrics();
    const summary = {
      totalQueries: 0,
      averageHitRate: 0,
      totalMemoryUsage: 0,
      totalInvalidations: 0,
      backgroundRefreshCount: 0
    };

    let totalHitRate = 0;
    let dataTypeCount = 0;

    metricsMap.forEach((metric) => {
      summary.totalQueries += metric.totalQueries;
      summary.totalMemoryUsage += metric.memoryUsage;
      summary.totalInvalidations += metric.invalidationCount;
      summary.backgroundRefreshCount += metric.backgroundRefreshCount;
      
      if (metric.totalQueries > 0) {
        totalHitRate += metric.hitRate;
        dataTypeCount++;
      }
    });

    summary.averageHitRate = dataTypeCount > 0 ? totalHitRate / dataTypeCount : 0;

    return summary;
  }, [getCacheMetrics]);

  const optimizeCache = useCallback(() => {
    service.optimizeMemoryUsage();
    service.cleanupExpiredEntries();
  }, [service]);

  return {
    metrics,
    getPerformanceSummary,
    optimizeCache
  };
};

/**
 * Hook for cache warming (preloading)
 */
export const useCacheWarming = () => {
  const { preloadCriticalData, service } = useCachingStrategy();

  const warmProductCache = useCallback(async (productHandles: string[]) => {
    const promises = productHandles.map(handle => 
      service.applyCachingStrategy(`product-detail-${handle}`, DataType.PRODUCT_DETAILS)
    );
    
    await Promise.allSettled(promises);
  }, [service]);

  const warmCollectionsCache = useCallback(async () => {
    service.applyCachingStrategy('collections-warm', DataType.COLLECTIONS);
  }, [service]);

  const warmHomepageCache = useCallback(async () => {
    service.applyCachingStrategy('homepage-warm', DataType.HOMEPAGE_DATA);
  }, [service]);

  return {
    preloadCriticalData,
    warmProductCache,
    warmCollectionsCache,
    warmHomepageCache
  };
};

// Export data types and triggers for external use
export { DataType, InvalidationTrigger };
export type { CacheStrategy };
